/**
 * IndexedDB storage abstraction (web platform).
 */

const DB_PREFIX = "story-builder-";

const storeRegistry = new Map<string, Set<string>>();
const dbConnections = new Map<string, Promise<IDBDatabase>>();

export function registerDb(dbName: string, storeNames: string[]): void {
  if (!storeRegistry.has(dbName)) {
    storeRegistry.set(dbName, new Set());
  }
  const set = storeRegistry.get(dbName)!;
  for (const name of storeNames) {
    set.add(name);
  }
}

function openAndMigrate(fullName: string, requiredStores: Set<string>): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const probe = indexedDB.open(fullName);
    probe.onsuccess = () => {
      const db = probe.result;
      const existing = db.objectStoreNames;
      const missing = [...requiredStores].filter((s) => !existing.contains(s));

      if (missing.length === 0) {
        resolve(db);
        return;
      }

      const nextVersion = db.version + 1;
      db.close();

      const upgrade = indexedDB.open(fullName, nextVersion);
      upgrade.onupgradeneeded = () => {
        const udb = upgrade.result;
        for (const storeName of requiredStores) {
          if (!udb.objectStoreNames.contains(storeName)) {
            udb.createObjectStore(storeName, { keyPath: "id" });
          }
        }
      };
      upgrade.onsuccess = () => resolve(upgrade.result);
      upgrade.onerror = () => reject(upgrade.error);
    };
    probe.onupgradeneeded = () => {
      const db = probe.result;
      for (const storeName of requiredStores) {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id" });
        }
      }
    };
    probe.onerror = () => reject(probe.error);
  });
}

function getDb(dbName: string): Promise<IDBDatabase> {
  const fullName = `${DB_PREFIX}${dbName}`;

  if (dbConnections.has(fullName)) {
    return dbConnections.get(fullName)!;
  }

  const stores = storeRegistry.get(dbName) || new Set<string>();
  const promise = openAndMigrate(fullName, stores);
  dbConnections.set(fullName, promise);
  return promise;
}

export async function getAll<T>(dbName: string, storeName: string): Promise<T[]> {
  const db = await getDb(dbName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getById<T>(dbName: string, storeName: string, id: string): Promise<T | undefined> {
  const db = await getDb(dbName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function put<T extends { id: string }>(dbName: string, storeName: string, item: T): Promise<void> {
  const db = await getDb(dbName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    store.put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function remove(dbName: string, storeName: string, id: string): Promise<void> {
  const db = await getDb(dbName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    store.delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearStore(dbName: string, storeName: string): Promise<void> {
  const db = await getDb(dbName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Close all open IndexedDB connections and clear the connection cache.
 * Call this when the tab is surrendering (e.g. session taken over by another tab)
 * so the new tab's IndexedDB operations are not blocked by this tab's connections.
 */
export function closeAll(): void {
  for (const [fullName, connPromise] of dbConnections) {
    connPromise.then((db) => db.close()).catch(() => {});
  }
  dbConnections.clear();
}

export async function deleteDb(dbName: string): Promise<void> {
  const fullName = `${DB_PREFIX}${dbName}`;
  const conn = dbConnections.get(fullName);
  if (conn) {
    try { (await conn).close(); } catch { /* ignore */ }
    dbConnections.delete(fullName);
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(fullName);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
